import { genUiT, type GenUiKey } from '../../shared/i18n_core.js';

/**
 * **Tipo do lugar → a primeira metade da segunda linha do campo `Onde`.**
 *
 * O designer pediu duas linhas por item (2026-08-06): nome em cima,
 * `tipo · endereço` embaixo — porque "Jardim Paulista" sozinho não diz se é
 * bairro, hotel ou escritório. O protótipo tinha três tipos porque o fixture só
 * tinha três; o motor devolve os tipos do Google, então a tradução acontece aqui.
 *
 * Vocabulário FECHADO e por PRECEDÊNCIA (o Google manda uma lista, e
 * `['lodging','point_of_interest','establishment']` é um hotel, não um "ponto de
 * interesse" genérico). Tipo fora da tabela não vira prefixo inventado: a linha
 * cai no endereço puro, que é o que o item já mostrava antes desta tabela existir.
 */
const PLACE_TYPE_KEYS: readonly (readonly [string, GenUiKey])[] = [
  ['airport', 'hotelPlanner.placeType.airport'],
  ['lodging', 'hotelPlanner.placeType.lodging'],
  ['hotel', 'hotelPlanner.placeType.lodging'],
  ['sublocality', 'hotelPlanner.placeType.neighborhood'],
  ['sublocality_level_1', 'hotelPlanner.placeType.neighborhood'],
  ['neighborhood', 'hotelPlanner.placeType.neighborhood'],
  ['locality', 'hotelPlanner.placeType.city'],
  ['administrative_area_level_2', 'hotelPlanner.placeType.city'],
  ['administrative_area_level_1', 'hotelPlanner.placeType.city'],
  ['tourist_attraction', 'hotelPlanner.placeType.poi'],
  ['point_of_interest', 'hotelPlanner.placeType.poi'],
  ['street_address', 'hotelPlanner.placeType.address'],
  ['route', 'hotelPlanner.placeType.address'],
  ['premise', 'hotelPlanner.placeType.address'],
];

/** O rótulo do tipo, ou `undefined` quando nenhum tipo conhecido veio. */
export function placeTypeLabel(
  types: readonly string[] | undefined,
  locale: string,
): string | undefined {
  if (!types?.length) return undefined;
  const known = new Set(types);
  const match = PLACE_TYPE_KEYS.find(([type]) => known.has(type));
  return match ? genUiT(locale, match[1]) : undefined;
}

/** A segunda linha inteira: `tipo · endereço`, com cada metade opcional. Sem as
 *  duas, não há segunda linha — e é isso que quem compõe a sugestão espera. */
export function placeSecondLine(
  types: readonly string[] | undefined,
  address: string | undefined,
  locale: string,
): string | undefined {
  const type = placeTypeLabel(types, locale);
  if (type && address) return `${type} · ${address}`;
  return type ?? address;
}

/** Tipos do Google que JÁ SÃO a cidade (ou algo maior): o nome do lugar diz
 *  onde ele fica, e "Curitiba · Curitiba, PR" seria ruído. Bairro, rua, hotel e
 *  ponto de interesse ficam de fora — são eles que precisam da cidade ao lado. */
const CITY_LEVEL_TYPES: ReadonlySet<string> = new Set([
  'locality',
  'postal_town',
  'colloquial_area',
  'administrative_area_level_3',
  'administrative_area_level_2',
  'administrative_area_level_1',
  'country',
]);

export function isCityLevelPlace(types: readonly string[] | null | undefined): boolean {
  return Boolean(types?.some((type) => CITY_LEVEL_TYPES.has(type)));
}

function comparable(text: string): string {
  return text
    .normalize('NFD')
    .replaceAll(/\p{Diacritic}/gu, '')
    .trim()
    .toLowerCase();
}

/**
 * **"Curitiba, PR" do lugar resolvido que NÃO é uma cidade** — o que o título
 * da busca e a pílula põem ao lado de "Avenida Paulista" para a pessoa ver EM
 * QUE cidade vai buscar (QA 29/09, A1: o homônimo de Curitiba só aparecia no
 * mapa). UF quando há; fora do Brasil sem estado, o país.
 *
 * `undefined` quando o lugar é a própria cidade, ou quando não há cidade para
 * dizer. Sem tipos (recente gravado antes deles), o rótulo que já começa pela
 * cidade ("São Paulo, SP") conta como cidade.
 */
export function placeLocality(
  place: {
    city?: string | undefined;
    state?: string | undefined;
    country?: string | undefined;
    types?: readonly string[] | undefined;
  },
  label: string | undefined,
): string | undefined {
  if (!place.city || isCityLevelPlace(place.types)) return undefined;
  if (!place.types?.length && label && comparable(label).startsWith(comparable(place.city))) {
    return undefined;
  }
  return [place.city, place.state ?? place.country].filter(Boolean).join(', ');
}

/** O glifo da linha. `history` é do GRUPO (procedência vence tipo — o que a
 *  linha não diz sozinha é "você já buscou isto", chamada do designer
 *  2026-08-19); os demais saem do tipo do lugar. */
export function placeIconFor(
  types: readonly string[] | undefined,
  fromHistory: boolean,
): 'history' | 'airport' | 'lodging' | 'place' {
  if (fromHistory) return 'history';
  const known = new Set(types ?? []);
  if (known.has('airport')) return 'airport';
  // `hotel` acompanha `lodging`: são o MESMO degrau na tabela de rótulos
  // (`PLACE_TYPE_KEYS` mapeia os dois pra "Hotel"), e glifo e rótulo têm de
  // contar a mesma categoria.
  if (known.has('lodging') || known.has('hotel')) return 'lodging';
  return 'place';
}
